import {cdnImage} from '@/util/cdnImage';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, {memo} from 'react';
import DisplayImage from '../display-image';
const rowLine = cdnImage('Hire-Blockchain-developer/arrow-line.webp');
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, gradients, fontWeight, fontSize, spacing, zIndex} from '@/styles/tokens';
import styles from './HireProcess.module.scss';

// ─── sx objects — module-level constants ──────────────────────────────────────
const hireProcessWrapperSx = {position: 'relative'} as const;

const backgroundColorBlueSx = {
    position: 'absolute',
    background: gradients.primaryActive,
    height: '100%',
    width: '50%',
    zIndex: zIndex.raised,
    '@media (max-width:1199px)': {display: 'none'}
} as const;

const backgroundColorGraySx = {
    position: 'absolute',
    background: 'unset',
    backgroundColor: '#f5f5f5',
    height: '100%',
    width: '50%',
    zIndex: zIndex.raised,
    right: 0
} as const;

const processContainerSx = {
    '@media (max-width:1199px)': {padding: 0}
} as const;

const hireProcessContentWrapperSx = {
    zIndex: zIndex.elevated,
    position: 'relative',
    '@media (max-width:1199px)': {pt: '30px', pb: '30px'}
} as const;

const processLeftSectionSx = {
    color: '#fff',
    '& a': {color: '#fff'},
    '@media (max-width:1199px)': {backgroundColor: colors.primary.main, py: '30px', px: spacing[16]}
} as const;

const processRightSectionSx = {
    '@media (max-width:1199px)': {backgroundColor: '#f5f5f5', py: '30px', px: spacing[16]}
} as const;

const processContainerHeadingWrapperSx = {px: spacing[20]} as const;

const processContainerSectionSx = {
    maxWidth: '350px',
    margin: 'auto',
    '@media (min-width:1441px)': {maxWidth: '500px'}
} as const;

const hireProcessHeadingSx = {
    fontSize: '27px',
    fontWeight: fontWeight.bold,
    '@media (max-width:600px)': {fontSize: '26px'}
} as const;

const hireProcessSubheadingSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: '27px'
} as const;

const listHireProcessHeadingSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold
} as const;

const listHireProcessSubheadingSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: '27px',
    '& a': {textDecoration: 'none'},
    '& a:hover': {textDecoration: 'underline'}
} as const;

const rowLineStyle = {width: '73%', height: 'auto'} as const;
const rowLineReverseStyle = {...rowLineStyle, transform: 'scaleX(-1)'} as const;

const HireProcess = (props: any) => {
    const {heading, subheading, content} = props;
    const mobileScreen = useMediaQuery('(max-width:1024px)');

    return (
        <Box className="hire-process-main-wrapper">
            {heading && (
                <Box className="spectrum-header-wrapper text-center">
                    <MUIContainer>
                        <Typography className="spectrum-heading color-black text-align-left">{heading}</Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </Box>
                        {subheading && (
                            <p className="solution-sub-heading color-black data_links spectrum-sub-heading text-align-left">
                                {subheading ? parse(subheading) : ''}
                            </p>
                        )}
                    </MUIContainer>
                </Box>
            )}
            <Box sx={hireProcessWrapperSx}>
                <Box sx={backgroundColorBlueSx}></Box>
                <Box sx={content?.length > 1 ? {...backgroundColorBlueSx, ...backgroundColorGraySx} : backgroundColorBlueSx}></Box>
                <MUIContainer sx={processContainerSx}>
                    <Grid
                        container
                        className={!mobileScreen ? 'top-space bottom-spacing' : 'mobile-bottom-padding'}
                        sx={hireProcessContentWrapperSx}
                    >
                        {content?.map((item: any, index: number) => (
                            <Grid md={6} xs={12} item key={index + 1} sx={index % 2 ? processRightSectionSx : processLeftSectionSx}>
                                <Box sx={processContainerHeadingWrapperSx} textAlign="center" mb={4}>
                                    <Typography className="font-headers" sx={hireProcessHeadingSx} mb={2}>
                                        {item.heading}
                                    </Typography>
                                    <Typography className="font-regular-text" sx={hireProcessSubheadingSx}>
                                        {item.subheading}
                                    </Typography>
                                </Box>
                                <Box sx={processContainerSectionSx}>
                                    {item?.data?.map((subChild: any, subIndex: number) => (
                                        <Box key={subIndex + 1} textAlign="center">
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={4}
                                                justifyContent="space-between"
                                                flexDirection={subIndex % 2 ? 'row-reverse' : 'row'}
                                            >
                                                <Box textAlign="left">
                                                    <Typography className="font-headers" sx={listHireProcessHeadingSx}>
                                                        {subChild.heading}
                                                    </Typography>
                                                    {subChild.subheading && (
                                                        <Typography
                                                            className="font-regular-text generative-transforming-subheading"
                                                            sx={listHireProcessSubheadingSx}
                                                        >
                                                            {subChild.subheading ? parse(subChild.subheading) : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <DisplayImage
                                                    src={subChild.src}
                                                    alt={subChild.heading ? subChild.alt : subChild.subheading}
                                                    className={styles.hireProcessImage}
                                                />
                                            </Box>
                                            {item?.data?.length !== subIndex + 1 && (
                                                <DisplayImage
                                                    src={rowLine}
                                                    alt="process-arrow-line"
                                                    style={subIndex % 2 ? rowLineReverseStyle : rowLineStyle}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>
        </Box>
    );
};

export default memo(HireProcess);
